import { User } from '../../types/cms.types';
import { UserService } from '../../service/supabase/UserService';
import IBloc from '../../base/IBloc';

export interface UserFilter {
    search?: string;
    location_city?: string;
    is_active?: boolean;
}

export interface UserState {
    users: User[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    loading: boolean;
    error: string | null;
    filters: UserFilter;
}

export interface UserViewState {
    users: User[];
    pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
    loading: boolean;
    error: string | null;
    filters: UserFilter;
    hasData: boolean;
    statistics?: {
        total: number;
        active: number;
        inactive: number;
    };
}

export class BlocManagementUsers extends IBloc {
    private userService: UserService;

    /**
     * State chính của Users Management
     */
    info: UserViewState = {
        users: [],
        pagination: {
            total: 0,
            page: 1,
            limit: 10,
            totalPages: 0
        },
        loading: false,
        error: null,
        filters: {},
        hasData: false,
        statistics: undefined
    };

    constructor() {
        super();
        this.userService = UserService.getInstance();
    }

    /**
     * Lấy dữ liệu ban đầu
     */
    async initData(): Promise<void> {
        this.updateInfo({ loading: true, error: null });

        try {
            await this.loadUsers();
            await this.loadStatistics();
        } catch (error: any) {
            console.error('Error initializing users data:', error);
            this.updateInfo({
                loading: false,
                error: error.message || 'Có lỗi xảy ra khi khởi tạo dữ liệu người dùng'
            });
        }
    }

    /**
     * Load users với pagination và filters
     */
    async loadUsers(): Promise<void> {
        this.updateInfo({ loading: true, error: null });

        try {
            const response = await this.userService.getUsers(
                this.info.pagination.page,
                this.info.pagination.limit,
                this.info.filters
            );

            if (response.success && response.data) {
                this.updateInfo({
                    users: response.data,
                    pagination: response.pagination,
                    loading: false,
                    error: null,
                    hasData: response.data.length > 0
                });
            } else {
                this.updateInfo({
                    users: [],
                    loading: false,
                    error: response.error || 'Không thể tải danh sách người dùng',
                    hasData: false
                });
            }
        } catch (error: any) {
            console.error('Error loading users:', error);
            this.updateInfo({
                users: [],
                loading: false,
                error: error.message || 'Có lỗi xảy ra khi tải danh sách người dùng',
                hasData: false
            });
        }
    }

    /**
     * Load statistics
     */
    async loadStatistics(): Promise<void> {
        try {
            const response = await this.userService.getUserStatistics();
            if (response.success && response.data) {
                this.updateInfo({
                    statistics: response.data
                });
            }
        } catch (error: any) {
            console.error('Error loading statistics:', error);
        }
    }

    /**
     * Lấy pagination hiện tại
     */
    get currentPagination() {
        return this.info.pagination;
    }

    /**
     * Thay đổi trang
     */
    async changePage(newPage: number): Promise<void> {
        if (newPage === this.info.pagination.page) {
            return;
        }

        this.updateInfo({
            pagination: {
                ...this.info.pagination,
                page: newPage
            }
        });

        await this.loadUsers();
    }

    /**
     * Thay đổi kích thước trang
     */
    async changePageSize(newLimit: number): Promise<void> {
        if (newLimit === this.info.pagination.limit) {
            return;
        }

        this.updateInfo({
            pagination: {
                ...this.info.pagination,
                page: 1,
                limit: newLimit
            }
        });

        await this.loadUsers();
    }

    /**
     * Filter by city
     */
    async filterByCity(city: string): Promise<void> {
        this.updateInfo({
            filters: {
                ...this.info.filters,
                location_city: city
            },
            pagination: {
                ...this.info.pagination,
                page: 1
            }
        });

        await this.loadUsers();
    }

    /**
     * Filter by active status
     */
    async filterByActiveStatus(isActive?: boolean): Promise<void> {
        this.updateInfo({
            filters: {
                ...this.info.filters,
                is_active: isActive
            },
            pagination: {
                ...this.info.pagination,
                page: 1
            }
        });

        await this.loadUsers();
    }

    /**
     * Tìm kiếm người dùng
     */
    async searchUsers(searchTerm: string): Promise<void> {
        this.updateInfo({
            filters: {
                ...this.info.filters,
                search: searchTerm
            },
            pagination: {
                ...this.info.pagination,
                page: 1
            }
        });

        await this.loadUsers();
    }

    /**
     * Xóa tất cả bộ lọc
     */
    async clearFilters(): Promise<void> {
        this.updateInfo({
            filters: {},
            pagination: {
                ...this.info.pagination,
                page: 1
            }
        });

        await this.loadUsers();
    }

    /**
     * Refresh dữ liệu
     */
    async refresh(): Promise<void> {
        await this.loadUsers();
        await this.loadStatistics();
    }

    /**
     * Tạo người dùng mới
     */
    async createUser(userData: Partial<User>): Promise<void> {
        this.updateInfo({ loading: true, error: null });

        try {
            const response = await this.userService.createUser(userData);

            if (response.success) {
                // Reload users list
                await this.loadUsers();
                await this.loadStatistics();
            } else {
                throw new Error(response.error || 'Không thể tạo người dùng');
            }
        } catch (error: any) {
            console.error('Error creating user:', error);
            this.updateInfo({
                loading: false,
                error: error.message || 'Có lỗi xảy ra khi tạo người dùng'
            });
            throw error;
        }
    }

    /**
     * Cập nhật người dùng
     */
    async updateUser(id: string, userData: Partial<User>): Promise<void> {
        this.updateInfo({ loading: true, error: null });

        try {
            const response = await this.userService.updateUser(id, userData);

            if (response.success) {
                // Update local state
                const updatedUsers = this.info.users.map(user =>
                    user.id === id ? { ...user, ...userData } : user
                );

                this.updateInfo({
                    users: updatedUsers,
                    loading: false,
                    error: null
                });

                await this.loadStatistics();
            } else {
                throw new Error(response.error || 'Không thể cập nhật người dùng');
            }
        } catch (error: any) {
            console.error('Error updating user:', error);
            this.updateInfo({
                loading: false,
                error: error.message || 'Có lỗi xảy ra khi cập nhật người dùng'
            });
            throw error;
        }
    }

    /**
     * Xóa người dùng
     */
    async deleteUser(id: string): Promise<void> {
        this.updateInfo({ loading: true, error: null });

        try {
            const response = await this.userService.deleteUser(id);

            if (response.success) {
                // Remove from local state
                const filteredUsers = this.info.users.filter(user => user.id !== id);

                this.updateInfo({
                    users: filteredUsers,
                    loading: false,
                    error: null
                });

                // Reload to update pagination
                await this.loadUsers();
                await this.loadStatistics();
            } else {
                throw new Error(response.error || 'Không thể xóa người dùng');
            }
        } catch (error: any) {
            console.error('Error deleting user:', error);
            this.updateInfo({
                loading: false,
                error: error.message || 'Có lỗi xảy ra khi xóa người dùng'
            });
            throw error;
        }
    }

    /**
     * Toggle user active status
     */
    async toggleUserStatus(id: string, isActive: boolean): Promise<void> {
        this.updateInfo({ loading: true, error: null });

        try {
            const response = await this.userService.toggleUserStatus(id, isActive);

            if (response.success) {
                // Update local state
                const updatedUsers = this.info.users.map(user =>
                    user.id === id ? { ...user, is_active: isActive } : user
                );

                this.updateInfo({
                    users: updatedUsers,
                    loading: false,
                    error: null
                });

                await this.loadStatistics();
            } else {
                throw new Error(response.error || 'Không thể thay đổi trạng thái người dùng');
            }
        } catch (error: any) {
            console.error('Error toggling user status:', error);
            this.updateInfo({
                loading: false,
                error: error.message || 'Có lỗi xảy ra khi thay đổi trạng thái người dùng'
            });
            throw error;
        }
    }

    /**
     * Update info state
     */
    private updateInfo(newInfo: Partial<UserViewState>): void {
        this.info = { ...this.info, ...newInfo };
        this.setStream('info', this.info);
    }

    /**
     * Cleanup khi component unmount
     */
    dispose(): void {
        super.dispose();
    }
}
